import { type CaraWorkspaceFieldsFragment } from '@oysterjs/ais/api/graphql';

/**
 * Pure helpers for the email body the workspace sends for some versions of an
 * artifact and not others.
 *
 * The subscription re-sends every version of every artifact on each tick, and
 * an email body carries pasted images inline as base64, so the server empties
 * the body of every version a later one has superseded
 * (`withoutSupersededEmailBodies`). The newest version keeps its body, which is
 * what lets a summary card's preview track the composer. A composer opened on a
 * superseded version fetches that version's body instead, and these helpers are
 * what stop the next tick throwing it away again — pure, so the race is
 * testable without a composer.
 */

type WorkspaceArtifact = CaraWorkspaceFieldsFragment['artifacts'][0];

/**
 * A workspace artifact as the focus rail holds it: every field a tick carries,
 * plus the email body the rail fetched for it. The body is optional because the
 * fetch lands after the rail has already opened on the artifact, and because
 * every other artifact type has none.
 */
export type FocusedArtifact = Omit<WorkspaceArtifact, 'email'> & {
  email?: (NonNullable<WorkspaceArtifact['email']> & { bodyHtml?: string }) | null;
};

/**
 * The body an artifact is carrying, or undefined when it is carrying none.
 *
 * An empty string counts as none, so empty means "ask for this version by id".
 * The one case that is wrong about — a draft whose author really did delete
 * every word — costs a fetch that comes back empty and nothing else.
 */
export const emailBodyOf = (artifact: FocusedArtifact | null | undefined): string | undefined =>
  artifact?.email?.bodyHtml || undefined;

/**
 * The artifact with a fetched body attached.
 *
 * Pass `contentRevision` from GetCaraWorkspaceEmailArtifact when hydrating a
 * superseded version: the tick's revision is hashed from the emptied body and
 * will not match the stored composition on save.
 *
 * Answers the artifact unchanged when it carries no email at all, so a caller
 * cannot invent an `email` on a PDF by handing this a body.
 */
export const withEmailBody = (
  artifact: FocusedArtifact,
  bodyHtml: string,
  contentRevision?: string
): FocusedArtifact =>
  artifact.email
    ? {
        ...artifact,
        email: {
          ...artifact.email,
          bodyHtml,
          ...(contentRevision !== undefined ? { contentRevision } : {})
        }
      }
    : artifact;

/**
 * Applies a newer copy of an artifact — a workspace tick, or the re-resolve the
 * focus rail does against the live workspace — to the one on screen.
 *
 * A superseded version's newer copy is newer in every respect but its body, so
 * the fetched body rides across, and only across. An incoming artifact that
 * brings a body of its own is the fuller of the two and wins outright, and one
 * describing a different artifact or version replaces the screen rather than
 * inheriting words that were never its own.
 */
export const applyWorkspaceTickToArtifact = (
  current: FocusedArtifact | null | undefined,
  incoming: FocusedArtifact
): FocusedArtifact => {
  if (!current) return incoming;
  if (current.id !== incoming.id || current.version !== incoming.version) return incoming;
  if (emailBodyOf(incoming) !== undefined) return incoming;

  const carried = emailBodyOf(current);
  if (carried === undefined) return incoming;

  return withEmailBody(incoming, carried, current.email?.contentRevision);
};
